import { Trace } from 'apollo-reporting-protobuf';
import classnames from 'classnames';
import {
  ASTKindToNode,
  DocumentNode,
  GraphQLFieldMap,
  GraphQLSchema,
  OperationDefinitionNode,
  Visitor,
  getVisitFn,
  isObjectType,
  parse,
} from 'graphql';
import { sum } from 'lodash';
import * as monacoEditor from 'monaco-editor';
import React from 'react';

import { printType } from 'src/app/graph/explorerPage/helpers/graphqlHelpers';
import { useActiveDefinitionDetails } from 'src/app/graph/explorerPage/hooks/useActiveDefinitionDetails';
import { ExplorerResult } from 'src/app/graph/explorerPage/hooks/useExplorerState/useExplorerResponses';
import { useDecodeTrace } from 'src/app/graph/operationsPage/report-pane/section-report/traces-section/queryTraces/histogramTraceFinder/queryTraceSection/traceProtobuf/useDecodeTrace';
import { format } from 'src/lib/format';
import { getOrSetRecord } from 'src/lib/getOrSetRecord';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';

import { EditorHint } from '../editorHint/EditorHint';
import { visitThroughFragments } from '../hintsHelpers';

function unwrapListTraces(traces: Trace.INode[]): Trace.INode[] {
  if (isMinLengthArray(1, traces) && !traces[0].type) {
    return traces.flatMap((trace) => unwrapListTraces(trace.child ?? []));
  }
  return traces;
}

function filterListTraces(
  traces: Trace.INode[],
  fields: GraphQLFieldMap<unknown, unknown>,
): Trace.INode[] {
  if (isMinLengthArray(1, traces) && !traces[0].type) {
    return traces.flatMap((trace) =>
      filterListTraces(trace.child ?? [], fields),
    );
  }
  // If all fields match, return the full set
  if (
    traces.every((trace) => {
      const name = trace.originalFieldName || trace.responseName;
      const field = name && name in fields && fields[name];
      return field && printType(field.type) === trace.type;
    })
  ) {
    return traces;
  }
  return [];
}

/**
 * Visit an ast while tracking the trace for each field.
 */
function visitWithTrace(
  tracesStack: Trace.INode[][],
  schema: GraphQLSchema,
  visitor: Visitor<ASTKindToNode>,
): Visitor<ASTKindToNode> {
  return {
    enter(node, ...rest) {
      const traces = tracesStack.slice(-1)[0] ?? [];
      switch (node.kind) {
        case 'Field': {
          const nodeName = node.name.value;
          const childTraces = traces
            .flatMap((trace) => unwrapListTraces(trace.child ?? []))
            .filter((child) => child.responseName === nodeName);

          if (!isMinLengthArray(1, childTraces)) {
            return false;
          }
          tracesStack.push(childTraces);
          break;
        }
        case 'InlineFragment':
        case 'FragmentDefinition': {
          const condition = node.typeCondition?.name.value;
          const filterType = condition && schema.getType(condition);
          // If we find a condition, filter to that tree
          if (condition && isObjectType(filterType)) {
            const filteredTraces =
              traces.map((trace) => {
                const fields = filterType.getFields();
                const filteredChildren =
                  trace.child && filterListTraces(trace.child, fields);
                return {
                  ...trace,
                  child: filteredChildren,
                };
              }) ?? [];

            tracesStack.push(filteredTraces);
          } else {
            tracesStack.push(traces);
          }
          break;
        }
        default: {
          break;
        }
      }
      const fn = getVisitFn(visitor, node.kind, false);
      return fn?.apply(visitor, [node, ...rest]);
    },
    leave(node, ...rest) {
      const fn = getVisitFn(visitor, node.kind, true);
      const result = fn?.apply(visitor, [node, ...rest]);
      switch (node.kind) {
        case 'Field':
        case 'InlineFragment':
        case 'FragmentDefinition': {
          tracesStack.pop();
          break;
        }
        default: {
          break;
        }
      }
      return result;
    },
  };
}

function addToTraceTimings(
  traceRoot: Trace,
  document: DocumentNode,
  schema: GraphQLSchema,
) {
  const hints: Record<number, { text: string; className?: string }[]> = {};
  const operation = document.definitions.find(
    (definition): definition is OperationDefinitionNode =>
      definition.kind === 'OperationDefinition',
  );

  if (!operation) {
    return [];
  }

  const tracesStack: Trace.INode[][] = [traceRoot.root ? [traceRoot.root] : []];

  visitThroughFragments(
    document,
    visitWithTrace(tracesStack, schema, {
      OperationDefinition: {
        enter(node) {
          const traces = tracesStack.slice(-1)[0];
          if (isMinLengthArray(1, traces) && node.loc) {
            const lineNumber = node.loc.startToken.line;
            getOrSetRecord(lineNumber, [], hints).push({
              text: `Total: ${format.ms(traceRoot.durationNs / (1000 * 1000))}`,
              className: 'mr-12',
            });
          }
        },
      },
      Field: {
        enter(node) {
          const traces = tracesStack.slice(-1)[0];
          if (isMinLengthArray(1, traces) && node.loc) {
            const lineNumber = node.loc.startToken.line;
            const durations = traces.map((trace) =>
              trace.startTime && trace.endTime
                ? trace.endTime - trace.startTime
                : 0,
            );
            getOrSetRecord(lineNumber, [], hints).push({
              text: format.ms(
                sum(durations) / (1000 * 1000 * durations.length),
              ),
            });
          }
        },
      },
    }),
  );

  return hints;
}

function operationsMatch(operation1: string, operation2: string) {
  return (
    operation1.trim().replace(/\s+/g, ' ') ===
    operation2.trim().replace(/\s+/g, ' ')
  );
}

export const TraceTimingHints = ({
  editor,
  schema,
  operationResult,
}: {
  editor: monacoEditor.editor.IStandaloneCodeEditor;
  schema: GraphQLSchema;
  operationResult: ExplorerResult;
}) => {
  const trace =
    operationResult?.type === 'response'
      ? (operationResult.data.trace ?? null)
      : null;
  const submittedOperationString =
    operationResult?.type === 'response'
      ? operationResult.operationString
      : undefined;
  const traceRoot = useDecodeTrace({ trace, operationName: null });

  const activeDefinition = useActiveDefinitionDetails(editor, schema);
  const activePartsOfDocument = activeDefinition?.isValid
    ? activeDefinition.activePartsOfDocument
    : undefined;
  const documentLength = activePartsOfDocument?.split('\n')?.length ?? 0;

  const hints = React.useMemo(() => {
    if (
      !traceRoot ||
      !activePartsOfDocument ||
      !submittedOperationString ||
      !operationsMatch(submittedOperationString, activePartsOfDocument)
    ) {
      return {};
    }
    return addToTraceTimings(traceRoot, parse(activePartsOfDocument), schema);
  }, [traceRoot, submittedOperationString, activePartsOfDocument, schema]);

  return (
    <>
      {Object.entries(hints).map(([lineNumber, hintsForLine]) => (
        <EditorHint
          // eslint-disable-next-line local/no-conditional-class-names
          className={classnames(hintsForLine.map((hint) => hint.className))}
          key={`${documentLength}-${lineNumber}-${hintsForLine}`}
          editor={editor}
          lineNumber={parseInt(lineNumber, 10)}
          hint={hintsForLine.map((hint) => hint.text).join(', ')}
        />
      ))}
    </>
  );
};
